import { Building2, Globe, Package } from "lucide-react";

export type ProhibitedKeywordTier = "global" | "brand" | "product";

interface TierOption {
  key: ProhibitedKeywordTier;
  icon: typeof Globe;
  label: string;
  description: string;
  disabled: boolean;
}

interface ProhibitedKeywordTierPickerProps {
  isAdmin: boolean;
  brandId: number | null;
  brandName: string;
  productId: number | null;
  productName?: string;
  onSelect: (tier: ProhibitedKeywordTier) => void;
}

// AUT-41: the tier-selection screen of AddProhibitedKeywordDialog, split out since the
// dialog also owns the tabbed add-form screen and the mutation/dispatch logic between them.
export function ProhibitedKeywordTierPicker({
  isAdmin,
  brandId,
  brandName,
  productId,
  productName,
  onSelect,
}: ProhibitedKeywordTierPickerProps) {
  const tiers: TierOption[] = [
    {
      key: "global",
      icon: Globe,
      label: "Global",
      description: isAdmin ? "Applies across every brand and product." : "Admins only.",
      disabled: !isAdmin,
    },
    {
      key: "brand",
      icon: Building2,
      label: "Brand-Specific",
      description: brandId ? `Applies only to ${brandName}.` : "No brand in context.",
      disabled: !brandId,
    },
    {
      key: "product",
      icon: Package,
      label: "Product-Specific",
      description: productId ? `Applies only to ${productName ?? "this product"}.` : "No product in context.",
      disabled: !productId,
    },
  ];

  return (
    <div className="space-y-2 pt-1">
      {tiers.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            type="button"
            disabled={t.disabled}
            onClick={() => onSelect(t.key)}
            className="w-full flex items-start gap-3 p-3 rounded-lg border border-border text-left transition-colors hover:bg-primary/10 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-border"
          >
            <div className="mt-0.5 h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0">
              <Icon className="h-4 w-4 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
